import {
  InternalRefetchQueriesInclude,
  useMutation,
  useQuery,
} from "@apollo/client";
import { useState } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import Placeholder from "@components/placeholder/placeholder";

import forkPlatformSkillMutation from "../../graphql/fork-platform-skill-mutation";
import platformSkillsQuery from "../../graphql/platform-skills-query";
import SkillEditModal from "./skill-edit-modal";

type PlatformSkillRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
};

type ForkedSkill = {
  id: string;
  name: string;
  description: string;
  body: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When provided, the fork creates a workspace skill in that org.
   * When omitted, the fork creates a personal skill.
   */
  organizationId?: number;
  /**
   * Queries to refetch after a successful fork. Caller-provided so this
   * component doesn't need to know about workspace/personal list queries.
   */
  refetchQueries?: InternalRefetchQueriesInclude;
};

const PlatformSkillsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
  refetchQueries,
}) => {
  const { data, loading } = useQuery<any>(platformSkillsQuery, {
    fetchPolicy: "cache-and-network",
    skip: !isOpen,
    variables: { organizationId: organizationId ?? null },
  });
  const [fork, { loading: forking }] = useMutation<any>(
    forkPlatformSkillMutation,
    { refetchQueries },
  );
  const [forkedSkill, setForkedSkill] = useState<ForkedSkill | null>(null);

  const skills: PlatformSkillRow[] = data?.platformSkills ?? [];

  const handleFork = async (skill: PlatformSkillRow) => {
    try {
      const result = await fork({
        variables: {
          slug: skill.slug,
          organizationId: organizationId ?? null,
        },
      });
      const created = result.data?.forkPlatformSkill?.skill;
      if (created) {
        const target = organizationId ? "your workspace" : "your skills";
        successNotificationVar({
          title: `Forked "${skill.name}" to ${target}`,
        });
        setForkedSkill({
          id: created.id,
          name: created.name,
          description: created.description,
          body: created.body,
        });
        onClose();
      }
    } catch (e) {
      errorNotificationVar({
        title: "Could not fork skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <>
      <SkillEditModal
        isOpen={!!forkedSkill}
        onClose={() => setForkedSkill(null)}
        initial={forkedSkill ?? undefined}
        organizationId={organizationId}
        refetchQueries={refetchQueries}
      />
      {isOpen && (
        <Modal
          open
          onClose={onClose}
          aria-label="Platform skills dialog"
          dialogClassName="md:max-w-3xl"
        >
          <div className="p-6 bg-white rounded-md">
            <div className="mb-4">
              <ModalTitle onClose={onClose}>Platform skills</ModalTitle>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Built-in skills provided by Topicflow. Fork one to customize it
              {organizationId
                ? " as a workspace skill available to everyone."
                : " as your own personal skill."}
            </p>

            {loading && skills.length === 0 ? (
              <div className="flex justify-center my-8">
                <Loading mini size="5" />
              </div>
            ) : skills.length === 0 ? (
              <Placeholder>No platform skills available.</Placeholder>
            ) : (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-2 font-medium">Skill</th>
                      <th className="px-4 py-2 font-medium w-1" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {skills.map((skill) => (
                      <tr
                        key={skill.id}
                        aria-label={`Platform skill row ${skill.name}`}
                      >
                        <td className="px-4 py-2 align-top">
                          <div className="font-mono text-blue-link">
                            /{skill.slug}
                          </div>
                          {skill.description && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {skill.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 align-top whitespace-nowrap">
                          <div className="flex gap-2 justify-end">
                            <Button
                              text="Test"
                              onClick={() =>
                                window.open(
                                  `/ask-ai?p=${encodeURIComponent(`/${skill.slug}`)}`,
                                  "_blank",
                                )
                              }
                            />
                            <Button
                              text="Fork"
                              disabled={forking}
                              onClick={() => handleFork(skill)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PlatformSkillsModal;
